/**
 * onlineTicketName.js — is this Square ticket name an online order?
 *
 * The one place this is decided: the Square dashboard (and the Order Alerts tab
 * that reuses it), the Order Status board and the Live Orders Completed filter
 * all call isOnlineTicketName.
 *
 * Counter staff type "online" into the ticket name by hand, fast, so it arrives
 * in every shape: "onlibe", "onlien", "pnline", "on!ine", "onilne"… A fixed
 * typo list can never keep up, so three checks run:
 *
 *   1. The original spellings list (substring match on the whole name), kept
 *      exactly as both pages had it, so nothing that matched before stops
 *      matching.
 *   2. "on line" / "on-line" written as two words.
 *   3. A typo check on each WORD: after undoing look-alike keys inside the
 *      word (0/9→o, !/1/|→l, ñ→n), a 5–8 letter word that is one edit from "online" counts
 *      (one wrong, missing or extra letter, or two neighbours swapped).
 *
 * Only one edit, and whole words only: allowing two edits, or matching the new
 * spellings as substrings, flagged ordinary words like "outline", "incline" and
 * "nonliberation" in a dictionary scan. One edit keeps every customer name in
 * the macOS proper-names list out.
 */

// The original list — substring matches, unchanged for backward compatibility.
export const ONLINE_KEYWORDS = [
  'online','onlkine','onmline','oniline','onkine','onlimne',
  'onlpine','onloine','onlinre','o nline','onlime','onpoine',
  'obnline','onlne','onli e','onliune','onnline','onnine',
  'ñnline','ññline','ññine','0nline',
  'onñine','onñlime','onlin',
];

// Whole-word shorthands only — as substrings these would hit real names.
const ONLINE_SHORT_WORDS = new Set(['onl', 'onln', 'oln']);
const TWO_WORD_RE = /(^|[^a-z])on[\s-]+line([^a-z]|$)/;
const TARGET = 'online';

/** Optimal-string-alignment distance (Levenshtein + adjacent transposition). */
function editDistance(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

/** Undo the look-alike keys staff hit instead of the real letter — only where
 *  one sits inside a word ("on!ine", "0nline"), so a trailing "!" or a table
 *  number stays punctuation. Lookahead only: no lookbehind on older iPad Safari. */
function normalize(name) {
  return name
    .replace(/\u00f1/g, 'n')
    .replace(/[09](?=[a-z])/g, 'o').replace(/([a-z])[09]/g, '$1o')
    .replace(/([a-z])[!1|](?=[a-z])/g, '$1l');
}

function wordLooksOnline(w) {
  if (ONLINE_SHORT_WORDS.has(w)) return true;
  return w.length >= 5 && w.length <= 8 && editDistance(w, TARGET) <= 1;
}

export function isOnlineTicketName(ticketName) {
  const name = (ticketName || '').toLowerCase();
  if (!name) return false;
  if (ONLINE_KEYWORDS.some(k => name.includes(k))) return true;
  if (TWO_WORD_RE.test(name)) return true;
  return normalize(name).split(/[^a-z]+/).some(wordLooksOnline);
}
